import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productAPI } from '../../api/product.api';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import ProductGrid from '../../components/product/ProductGrid';
import Loader from '../../components/common/Loader';
import {
  EYEGLASSES_PRESCRIPTION_OPTIONS,
  FILTER_GENDERS,
  FILTER_MATERIALS,
  PRESENT_TYPE_OPTIONS,
} from '../../constants/catalog';
import { getDisplayPrice, getSalePercentage } from '../../utils/productPricing';
import { getProductMaterials, productMatchesMaterials, productMatchesSelectedGenders } from '../../utils/catalogOptions';
import { getCompactPaginationItems } from '../../utils/pagination';
import { scrollToPageTop } from '../../components/common/ScrollToTop';
import './ProductList.css';

const CATALOG_SORT_OPTIONS = [
  { labelKey: 'catalog.featured', value: 'default' },
  { labelKey: 'catalog.bestSelling', value: 'bestselling' },
  { labelKey: 'catalog.newArrivals', value: 'newest' },
  { labelKey: 'catalog.saleHighLow', value: 'sale-desc' },
  { labelKey: 'catalog.priceLowHigh', value: 'price-asc' },
  { labelKey: 'catalog.priceHighLow', value: 'price-desc' },
];

const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const getSearchableProductText = (product = {}) => [
  product.name,
  product.title,
  product.brand,
  product.category,
  product.description,
  product.specialCode,
  product.productCode,
  product.code,
  ...getProductMaterials(product),
].map(normalizeValue).join(' ');
const genderLabelKey = (sex) => `catalog.${normalizeValue(sex)}`;
const getBrandParams = (params) => {
  const directBrands = params.getAll('brand');
  const groupedBrands = String(params.get('brands') || '')
    .split(',')
    .map((brand) => brand.trim())
    .filter(Boolean);

  return Array.from(new Set([...directBrands, ...groupedBrands].map((brand) => String(brand || '').trim()).filter(Boolean)));
};

const getSalePercentageParam = (params) => {
  const value = Number(
    params.get('salePercentage') ||
    params.get('sale') ||
    params.get('discount') ||
    0
  );

  return Number.isFinite(value) && value > 0 ? value : 0;
};

const getProductPrescriptionOptions = (product) => {
  const options = Array.isArray(product.prescriptionOptions)
    ? product.prescriptionOptions
    : [product.prescription, product.power];

  return options.map((option) => String(option || '').trim()).filter(Boolean);
};

const getProductPresentType = (product) => normalizeValue(
  product.presentType ||
  product.presentSubtype ||
  product.giftType ||
  product.subcategory ||
  product.type
);

const ProductList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamString = searchParams.toString();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSexes, setSelectedSexes] = useState([]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState([]);
  const [selectedPresentTypes, setSelectedPresentTypes] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const { addToCart, addToWishlist } = useCartWishlist();
  const { t } = useLanguage();
  const selectedCategory = searchParams.get('category') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const selectedSalePercentage = useMemo(
    () => getSalePercentageParam(new URLSearchParams(searchParamString)),
    [searchParamString]
  );
  const selectedBrands = useMemo(
    () => getBrandParams(new URLSearchParams(searchParamString)),
    [searchParamString]
  );
  const categoryTranslationKey = `category.${normalizeValue(selectedCategory)}`;
  const translatedCategory = t(categoryTranslationKey);
  const categoryTitle = selectedCategory === 'all'
    ? t('category.allProducts')
    : (translatedCategory !== categoryTranslationKey ? translatedCategory : selectedCategory.replace(/[-_]/g, ' ')).toUpperCase();
  const normalizedSelectedCategory = normalizeValue(selectedCategory);
  const isEyeglassesCategory = normalizedSelectedCategory === 'eyeglasses';
  const isPresentsCategory = normalizedSelectedCategory === 'presents';
  const shouldShowGenderFilter = normalizedSelectedCategory !== 'knives';
  const scopedProducts = useMemo(
    () => products.filter((product) => selectedCategory === 'all' || normalizeValue(product.category) === normalizedSelectedCategory),
    [products, selectedCategory, normalizedSelectedCategory]
  );
  const categoryBrands = useMemo(() => Array.from(
    new Set(
      scopedProducts
        .map((product) => product.brand)
        .filter(Boolean)
        .map((brand) => String(brand).trim())
        .filter(Boolean)
    )
  ).sort((first, second) => first.localeCompare(second)), [scopedProducts]);
  const categoryMaterials = useMemo(() => Array.from(
    new Set(
      scopedProducts
        .flatMap(getProductMaterials)
        .map((material) => String(material).trim())
        .filter(Boolean)
    )
  ), [scopedProducts]);
  const sidebarBrands = useMemo(() => categoryBrands.filter((brand) =>
    brand.toLowerCase().includes(brandSearch.toLowerCase())
  ), [brandSearch, categoryBrands]);
  const activeSelectedBrands = useMemo(() => {
    const availableBrandKeys = new Set(categoryBrands.map((brand) => normalizeValue(brand)));
    return selectedBrands.filter((brand) => availableBrandKeys.has(normalizeValue(brand)));
  }, [categoryBrands, selectedBrands]);
  const sidebarMaterials = useMemo(() => Array.from(new Set([...FILTER_MATERIALS, ...categoryMaterials])), [categoryMaterials]);
  const priceFloor = scopedProducts.length ? Math.floor(Math.min(...scopedProducts.map((product) => getDisplayPrice(product)))) : 0;
  const priceCeiling = scopedProducts.length ? Math.ceil(Math.max(...scopedProducts.map((product) => getDisplayPrice(product)))) : 999;
  const priceSpan = Math.max(priceCeiling - priceFloor, 1);
  const priceRangeStyle = {
    '--price-start': `${((minPrice - priceFloor) / priceSpan) * 100}%`,
    '--price-end': `${((maxPrice - priceFloor) / priceSpan) * 100}%`,
  };
  const hasBrandFilter = categoryBrands.length > 0;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / 9));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * 9, currentPage * 9);
  const paginationItems = useMemo(() => getCompactPaginationItems(totalPages, currentPage), [totalPages, currentPage]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...products];

    if (searchQuery) {
      const queryWords = normalizeValue(searchQuery).split(/\s+/).filter(Boolean);
      filtered = filtered.filter(
        (product) => {
          const searchableText = getSearchableProductText(product);
          return queryWords.every((word) => searchableText.includes(word));
        }
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((product) => normalizeValue(product.category) === normalizeValue(selectedCategory));
    }

    if (selectedSalePercentage > 0) {
      filtered = filtered.filter((product) => Math.abs(getSalePercentage(product) - selectedSalePercentage) < 0.001);
    }

    filtered = filtered.filter((product) => {
      const price = getDisplayPrice(product);
      return price >= minPrice && price <= maxPrice;
    });

    if (activeSelectedBrands.length) {
      filtered = filtered.filter((product) => activeSelectedBrands.some((brand) => normalizeValue(brand) === normalizeValue(product.brand)));
    }

    if (shouldShowGenderFilter && selectedSexes.length) {
      filtered = filtered.filter((product) => productMatchesSelectedGenders(product, selectedSexes));
    }

    if (isPresentsCategory && selectedPresentTypes.length) {
      filtered = filtered.filter((product) => selectedPresentTypes.some((type) => normalizeValue(type) === getProductPresentType(product)));
    }

    if (selectedMaterials.length) {
      filtered = filtered.filter((product) => productMatchesMaterials(product, selectedMaterials));
    }

    if (isEyeglassesCategory && selectedPrescriptions.length) {
      filtered = filtered.filter((product) => {
        const productOptions = getProductPrescriptionOptions(product);

        return selectedPrescriptions.some((prescription) => productOptions.includes(prescription));
      });
    }

    switch (sortBy) {
      case 'bestselling':
        filtered.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0));
        break;
      case 'price-asc':
        filtered.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      case 'price-desc':
        filtered.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
        break;
      case 'name-asc':
        filtered.sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
        break;
      case 'name-desc':
        filtered.sort((a, b) => (b.title || b.name || '').localeCompare(a.title || a.name || ''));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'sale-desc':
        filtered.sort((a, b) => getSalePercentage(b) - getSalePercentage(a));
        break;
      default:
        break;
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, selectedSalePercentage, sortBy, searchQuery, activeSelectedBrands, selectedSexes, selectedPresentTypes, selectedMaterials, selectedPrescriptions, minPrice, maxPrice, isEyeglassesCategory, isPresentsCategory, shouldShowGenderFilter]);

  useEffect(() => {
    setMinPrice(priceFloor);
    setMaxPrice(priceCeiling);
  }, [priceFloor, priceCeiling, selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredProducts.length]);

  useEffect(() => {
    if (!shouldShowGenderFilter && selectedSexes.length) {
      setSelectedSexes([]);
    }
  }, [selectedSexes.length, shouldShowGenderFilter]);

  useEffect(() => {
    if (!isPresentsCategory && selectedPresentTypes.length) {
      setSelectedPresentTypes([]);
    }
  }, [isPresentsCategory, selectedPresentTypes.length]);

  const fetchData = async () => {
    try {
      const productsList = await productAPI.getAllAvailable();
      setProducts(productsList);
    } catch (error) {
      console.error('Error fetching data:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (value, setter) => {
    setter((current) => (current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]));
  };

  const changePage = (getNextPage) => {
    setCurrentPage((page) => {
      const requestedPage = typeof getNextPage === 'function' ? getNextPage(page) : getNextPage;
      const nextPage = Math.min(totalPages, Math.max(1, requestedPage));

      if (nextPage !== page) {
        window.requestAnimationFrame(scrollToPageTop);
      }

      return nextPage;
    });
  };

  const replaceCatalogParams = (configure) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    configure(nextSearchParams);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const toggleBrand = (brand) => {
    const isSelected = activeSelectedBrands.some((entry) => normalizeValue(entry) === normalizeValue(brand));
    const nextBrands = isSelected
      ? activeSelectedBrands.filter((entry) => normalizeValue(entry) !== normalizeValue(brand))
      : [...activeSelectedBrands, brand];

    replaceCatalogParams((nextSearchParams) => {
      nextSearchParams.delete('brand');
      nextSearchParams.delete('brands');
      nextBrands.forEach((selectedBrand) => {
        nextSearchParams.append('brand', selectedBrand);
      });
    });
  };

  const clearFilters = () => {
    setSortBy('default');
    setShowFilters(false);
    setSelectedSexes([]);
    setSelectedMaterials([]);
    setSelectedPrescriptions([]);
    setSelectedPresentTypes([]);
    setBrandSearch('');
    setMinPrice(priceFloor);
    setMaxPrice(priceCeiling);
    setCurrentPage(1);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="products-page">
      <section className="catalog-shell catalog-shell--hero">
        <div className="catalog-hero">
          <h1 className="catalog-hero__title">{categoryTitle}</h1>
          <div className="catalog-hero__meta">{filteredProducts.length} {t('common.products')}</div>

          <button className="mobile-filter-toggle" type="button" onClick={() => setShowFilters((open) => !open)}>
            {t('catalog.filters')}
          </button>
        </div>
      </section>

      <section className="catalog-shell">
        <div className="products-container">
          <aside className={`filters-sidebar ${showFilters ? 'show' : ''}`}>
            <div className="filters-header">
              <h3>{t('catalog.sortBy')}</h3>
              <button className="filters-close" type="button" onClick={() => setShowFilters(false)} aria-label={t('common.closeFilters')}>
                ×
              </button>
            </div>

            <div className="filter-group filter-group--choices">
              {CATALOG_SORT_OPTIONS.map((option) => (
                <label key={option.value} className="filter-check">
                  <input
                    type="checkbox"
                    checked={sortBy === option.value}
                    onChange={() => setSortBy(sortBy === option.value ? 'default' : option.value)}
                  />
                  <span>{t(option.labelKey)}</span>
                </label>
              ))}
            </div>

            {shouldShowGenderFilter && (
              <>
                <div className="filters-header filters-header--section">
                  <h3>{t('catalog.sex')}</h3>
                </div>

                <div className="filter-group filter-group--choices">
                  {FILTER_GENDERS.map((sex) => (
                    <label key={sex} className="filter-check">
                      <input type="checkbox" checked={selectedSexes.includes(sex)} onChange={() => toggleSelection(sex, setSelectedSexes)} />
                      <span>{t(genderLabelKey(sex))}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {isPresentsCategory && (
              <>
                <div className="filters-header filters-header--section">
                  <h3>{t('catalog.giftType')}</h3>
                </div>

                <div className="filter-group filter-group--choices">
                  {PRESENT_TYPE_OPTIONS.map((presentType) => (
                    <label key={presentType.value} className="filter-check">
                      <input
                        type="checkbox"
                        checked={selectedPresentTypes.includes(presentType.value)}
                        onChange={() => toggleSelection(presentType.value, setSelectedPresentTypes)}
                      />
                      <span>{t(presentType.labelKey)}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {hasBrandFilter && (
              <>
                <div className="filters-header filters-header--section">
                  <h3>{t('catalog.brands')}</h3>
                </div>

                <div className="filter-group">
                  <div className="brands-search">
                    <input
                      type="text"
                      value={brandSearch}
                      onChange={(event) => setBrandSearch(event.target.value)}
                      placeholder={t('common.search')}
                      className="brands-search__input"
                    />
                    <span className="brands-search__icon" aria-hidden="true">
                      <svg width="22" height="22" viewBox="0 0 30 30" fill="none">
                        <circle cx="13" cy="13" r="8.5" stroke="currentColor" strokeWidth="2.2" />
                        <path d="M19.5 19.5L25 25" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    </span>
                  </div>

                  <div className="filter-group filter-group--choices filter-group--brands">
                    {sidebarBrands.map((brand) => (
                      <label key={brand} className="filter-check">
                        <input
                          type="checkbox"
                          checked={activeSelectedBrands.some((entry) => normalizeValue(entry) === normalizeValue(brand))}
                          onChange={() => toggleBrand(brand)}
                        />
                        <span>{brand}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {isEyeglassesCategory && (
              <>
                <div className="filters-header filters-header--section">
                  <h3>{t('catalog.prescription')}</h3>
                </div>

                <div className="filter-group filter-group--prescriptions" aria-label={t('catalog.prescription')}>
                  {EYEGLASSES_PRESCRIPTION_OPTIONS.map((prescription) => (
                    <button
                      key={prescription}
                      type="button"
                      className={`prescription-filter ${selectedPrescriptions.includes(prescription) ? 'is-active' : ''}`}
                      onClick={() => toggleSelection(prescription, setSelectedPrescriptions)}
                    >
                      {prescription}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="filters-header filters-header--section">
              <h3>{t('catalog.price')}</h3>
            </div>

            <div className="filter-group">
              <div className="price-range" style={priceRangeStyle}>
                <span className="price-range__selection" aria-hidden="true" />
                <input
                  type="range"
                  className="price-range__input price-range__input--min"
                  min={priceFloor}
                  max={priceCeiling}
                  step="1"
                  value={minPrice}
                  onChange={(event) => setMinPrice(Math.min(Number(event.target.value), maxPrice))}
                  aria-label="Minimum price"
                />
                <input
                  type="range"
                  className="price-range__input price-range__input--max"
                  min={priceFloor}
                  max={priceCeiling}
                  step="1"
                  value={maxPrice}
                  onChange={(event) => setMaxPrice(Math.max(Number(event.target.value), minPrice))}
                  aria-label="Maximum price"
                />
              </div>

              <div className="price-values">
                <label>
                  <input
                    type="number"
                    min={priceFloor}
                    max={maxPrice}
                    value={minPrice}
                    onChange={(event) => setMinPrice(Math.max(priceFloor, Math.min(Number(event.target.value), maxPrice)))}
                    aria-label="Minimum price value"
                  />
                  <span>₾</span>
                </label>
                <label>
                  <input
                    type="number"
                    min={minPrice}
                    max={priceCeiling}
                    value={maxPrice}
                    onChange={(event) => setMaxPrice(Math.min(priceCeiling, Math.max(Number(event.target.value), minPrice)))}
                    aria-label="Maximum price value"
                  />
                  <span>₾</span>
                </label>
              </div>
            </div>

            <div className="filters-header filters-header--section">
              <h3>{t('catalog.materials')}</h3>
            </div>

            <div className="filter-group filter-group--choices">
              {sidebarMaterials.map((material) => (
                <label key={material} className="filter-check">
                  <input
                    type="checkbox"
                    checked={selectedMaterials.includes(material)}
                    onChange={() => toggleSelection(material, setSelectedMaterials)}
                  />
                  <span>{material}</span>
                </label>
              ))}
            </div>

            <button className="clear-filters" type="button" onClick={clearFilters}>
              {t('catalog.clearAll')}
            </button>
          </aside>

          <div className="products-main">
            {paginatedProducts.length > 0 ? (
              <ProductGrid products={paginatedProducts} onAddToCart={addToCart} onAddToWishlist={addToWishlist} />
            ) : (
              <div className="no-products">
                <span>⌕</span>
                <h3>{t('catalog.noProducts')}</h3>
                <p>{t('catalog.noProductsHint')}</p>
                <button type="button" onClick={clearFilters} className="reset-button">
                  {t('catalog.resetFilters')}
                </button>
              </div>
            )}

            {filteredProducts.length > 0 && (
              <div className="pagination" aria-label="Pagination">
                <button type="button" className="pagination__arrow" onClick={() => changePage(1)} disabled={currentPage === 1}>
                  «
                </button>
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={() => changePage((page) => page - 1)}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                {paginationItems.map((item) => {
                  if (typeof item !== 'number') {
                    return (
                      <span key={item} className="pagination__ellipsis" aria-hidden="true">
                        ...
                      </span>
                    );
                  }

                  return (
                    <button
                      key={item}
                      type="button"
                      className={`pagination__page ${item === currentPage ? 'is-active' : ''}`}
                      onClick={() => changePage(item)}
                      aria-current={item === currentPage ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={() => changePage((page) => page + 1)}
                  disabled={currentPage === totalPages}
                >
                  ›
                </button>
                <button type="button" className="pagination__arrow" onClick={() => changePage(totalPages)} disabled={currentPage === totalPages}>
                  »
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductList;
